/**
 * outlineMerge - Merge server-fetched outline into local state
 *
 * When the user has pending local edits (outlineLocalEditTimestamp is set),
 * we cannot blindly replace the local outline with the server response.
 * Instead, we merge: take server-owned fields from the server response
 * and preserve user-editable fields from the local state.
 */

import type { VideoOutline, SlideOutline } from '@/types/outline';

// Fields that only the server/backend sets (generation pipeline, status tracking)
const SERVER_OWNED_SLIDE_FIELDS: (keyof SlideOutline)[] = [
  'slideConversationId',
  'generationStatus',
  'generationError',
  'generatingThinking',
  'generatedAt',
  'generatedThumbnail',
  'generationStartedAt',
  'isStuck',
  'isDeleted',
  'deletedAt',
  // Audio-split outputs: when the worker re-slices a seam, the SSE refetch must
  // overlay the server's new split/range/lock so the storyboard + audio preview
  // update live (without these, the merge keeps stale local copies → page reload).
  'audioRange',
  'seam',
  'boundaryLock',
];

/**
 * Merges a server-fetched outline into the local outline state.
 *
 * - Server-owned top-level fields (status, approvedAt) come from server.
 * - User-editable top-level fields (title, branding, templateId, templateName) are preserved from local.
 * - Per-slide: server-owned fields come from server, user-editable fields preserved from local.
 * - Slides that exist on server but not locally are appended (e.g., slides added via AI chat).
 */
export function mergeServerOutlineIntoLocal(
  local: VideoOutline,
  server: VideoOutline
): VideoOutline {
  // Build a map of local slides by ID for fast lookup
  const localSlideMap = new Map<string, SlideOutline>();
  for (const slide of local.slides) {
    localSlideMap.set(slide.id, slide);
  }

  // Merge slides: iterate server slides to preserve server ordering and catch new slides
  const mergedSlides: SlideOutline[] = server.slides.map((serverSlide) => {
    const localSlide = localSlideMap.get(serverSlide.id);

    if (!localSlide) {
      // Slide exists on server but not locally - take it entirely from server
      return serverSlide;
    }

    // Merge: start from local slide, overlay server-owned fields
    const merged: SlideOutline = { ...localSlide };

    for (const field of SERVER_OWNED_SLIDE_FIELDS) {
      if (field in serverSlide) {
        (merged as any)[field] = (serverSlide as any)[field];
      }
    }

    // Preserve order from server (server handles reordering/renumbering)
    merged.order = serverSlide.order;

    return merged;
  });

  return {
    // Preserve user-editable top-level fields from local
    ...local,
    // Take server-owned top-level fields from server
    status: server.status,
    approvedAt: server.approvedAt,
    generatedAt: server.generatedAt,
    // Use merged slides
    slides: mergedSlides,
  };
}
